import { Building } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { toast } from "react-toastify";
import api from "~/services/api";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../..";
import { AlertContext } from "~/App";
import { format, parseISO } from "date-fns";

export const InputContext = createContext({});

export default function CancelActivation({
  access,
  id,
  defaultFormType = "preview",
  selected,
  handleOpened,
}) {
  const { successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const { alertBox } = useContext(AlertContext);
  const [pageData, setPageData] = useState({ loaded: false });
  const [loading, setLoading] = useState(false);
  const [registry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const generalForm = useRef();

  function handleCloseForm() {
    handleOpened(null);
  }

  async function handleCancel() {
    alertBox({
      title: "Attention!",
      descriptionHTML: `<p>Are you sure you want to cancel the activation of <strong>${pageData.name} ${pageData.last_name}</strong>?</p>`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes, cancel",
          onPress: async () => {
            setLoading(true);
            try {
              await api.post(`/students/cancel-activation/${selected[0].id}`);
              toast("Activation cancelled!", { autoClose: 1000 });
              setSuccessfullyUpdated(true);
              handleOpened(null);
            } catch (err) {
              toast(err?.response?.data?.error || "Error cancelling activation", {
                type: "error",
                autoClose: 3000,
              });
            } finally {
              setLoading(false);
            }
          },
        },
      ],
    });
  }

  useEffect(() => {
    async function loadData() {
      const { data } = await api.get(`/students/${selected[0].id}`);
      setPageData({ ...data, loaded: true });
    }
    loadData();
  }, []);

  const studentxgroup = pageData.studentxgroups?.[0];

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <Building size={16} /> General
            </RegisterFormMenu>
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
              <form
                ref={generalForm}
                onSubmit={(e) => e.preventDefault()}
                className="w-full"
              >
                <InputContext.Provider
                  value={{
                    id,
                    generalForm,
                    setSuccessfullyUpdated,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    handleCloseForm,
                  }}
                >
                  {pageData.loaded ? (
                    <>
                      <FormHeader
                        access={access}
                        title={
                          "Cancel Activation - " +
                          pageData.name +
                          " " +
                          pageData.last_name
                        }
                        loading={loading}
                        registry={registry}
                        InputContext={InputContext}
                      />

                      <InputLineGroup
                        title="PENDING ACTIVATION"
                        activeMenu={activeMenu === "general"}
                      >
                        <InputLine title="Expected Group">
                          <div className="flex flex-col gap-1 py-2">
                            <span className="text-xs text-gray-500">Group</span>
                            <span className="text-sm font-semibold">
                              {studentxgroup?.group?.name || "-"}
                            </span>
                          </div>
                        </InputLine>
                        <InputLine title="Details">
                          <div className="flex flex-col gap-1 py-2">
                            <span className="text-xs text-gray-500">
                              Start Date
                            </span>
                            <span className="text-sm font-semibold">
                              {studentxgroup?.start_date
                                ? format(parseISO(studentxgroup.start_date), "MM/dd/yyyy")
                                : "-"}
                            </span>
                          </div>
                        </InputLine>
                        <InputLine title="Action">
                          <div className="py-2">
                            <button
                              type="button"
                              onClick={handleCancel}
                              disabled={loading}
                              className="px-4 py-2 bg-red-600 text-white text-xs font-semibold rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                              Cancel Activation
                            </button>
                          </div>
                        </InputLine>
                      </InputLineGroup>
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </InputContext.Provider>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
